<template>
  <section class="supplier-detail-tabs-panel">
    <AppSectionTabs
      v-if="showSubTabs"
      v-model="currentSubTab"
      :tabs="currentSubTabs"
      :aria-label="text.detail.tabsPanel.subSectionsAriaLabel"
    />

    <SupplierDetailOverviewTab
      v-if="activeTab === 'summary'"
      :supplier="supplier"
      :supplier-overview="supplierOverview"
      @open-history="$emit('open-history')"
    />

    <SupplierDetailSummaryTab
      v-else-if="activeTab === 'contact-billing'"
      :supplier="supplier"
      :persons="persons"
      :default-billing-data="defaultBillingData"
      @edit-section="$emit('edit-section', $event)"
      @add-contact="$emit('add-contact')"
      @edit-contact="$emit('edit-contact', $event)"
      @delete-contact="$emit('delete-contact', $event)"
      @edit-billing-data="$emit('edit-billing-data')"
      @open-billing-data-list="$emit('open-billing-data-list')"
      @create-billing-data="$emit('create-billing-data')"
    />

    <SupplierDetailExpensesTab
      v-else-if="activeTab === 'operation' && currentSubTab === 'expenses'"
      :rows="expenseRows"
      :loading="expenseLoading"
      :filter-options="expenseFilterOptions"
      :pagination="expensePagination"
      :status-tabs="expenseStatusTabs"
      :active-status-tab="expenseActiveStatusTab"
      :empty-text="text.detail.operation.expenses.table.empty"
      @search-change="$emit('expenses-search-change', $event)"
      @filters-change="$emit('expenses-filters-change', $event)"
      @status-tab-select="$emit('expenses-status-tab-select', $event)"
      @page-change="$emit('expenses-page-change', $event)"
      @pay-expense="$emit('pay-expense', $event)"
      @edit-expense="$emit('edit-expense', $event)"
      @delete-expense="$emit('delete-expense', $event)"
      @approve-expense="$emit('approve-expense', $event)"
      @download-xml="$emit('download-xml', $event)"
      @download-pdf="$emit('download-pdf', $event)"
      @download-receipt="$emit('download-receipt', $event)"
    />

    <SupplierDetailFeesTab
      v-else-if="activeTab === 'operation' && currentSubTab === 'commissions'"
      :rows="feeRows"
      :loading="feeLoading"
      :filter-options="feeFilterOptions"
      :pagination="feePagination"
      :status-tabs="feeStatusTabs"
      :active-status-tab="feeActiveStatusTab"
      :empty-text="text.detail.operation.fees.table.empty"
      @search-change="$emit('fees-search-change', $event)"
      @filters-change="$emit('fees-filters-change', $event)"
      @status-tab-select="$emit('fees-status-tab-select', $event)"
      @page-change="$emit('fees-page-change', $event)"
      @pay-expense="$emit('pay-fee', $event)"
      @edit-expense="$emit('edit-fee', $event)"
      @delete-expense="$emit('delete-fee', $event)"
      @approve-expense="$emit('approve-fee', $event)"
      @download-xml="$emit('download-fee-xml', $event)"
      @download-pdf="$emit('download-fee-pdf', $event)"
      @download-receipt="$emit('download-fee-receipt', $event)"
    />

    <SupplierDetailDocumentsTab
      v-else-if="activeTab === 'documents'"
      :supplier="supplier"
    />

    <SupplierDetailAdvancedOptionsTab
      v-else-if="activeTab === 'settings'"
      :supplier="supplier"
      @open-merge="$emit('open-merge')"
      @archive-toggle="$emit('archive-toggle')"
      @delete-supplier="$emit('delete-supplier')"
    />

    <SupplierDetailHistoryTab
      v-else-if="activeTab === 'history'"
      :supplier="supplier"
    />

    <SupplierDetailPlaceholderTab
      v-else
      :title="currentTabTitle"
    />
  </section>
</template>

<script>
import AppSectionTabs from '@/components/common/AppSectionTabs.vue';
import SupplierDetailOverviewTab from '@/components/suppliers/detail/tabs/SupplierDetailOverviewTab.vue';
import SupplierDetailSummaryTab from '@/components/suppliers/detail/tabs/SupplierDetailSummaryTab.vue';
import SupplierDetailHistoryTab from '@/components/suppliers/detail/tabs/SupplierDetailHistoryTab.vue';
import SupplierDetailPlaceholderTab from '@/components/suppliers/detail/tabs/SupplierDetailPlaceholderTab.vue';
import SupplierDetailExpensesTab from '@/components/suppliers/detail/tabs/SupplierDetailExpensesTab.vue';
import SupplierDetailFeesTab from '@/components/suppliers/detail/tabs/SupplierDetailFeesTab.vue';
import SupplierDetailDocumentsTab from '@/components/suppliers/detail/tabs/SupplierDetailDocumentsTab.vue';
import SupplierDetailAdvancedOptionsTab from '@/components/suppliers/detail/tabs/SupplierDetailAdvancedOptionsTab.vue';
import {
  createSupplierDetailAnalysisSubTabs,
  createSupplierDetailFinanceSubTabs,
  createSupplierDetailOperationSubTabs,
} from '@/composables/suppliers/useSupplierDetailTabs';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: suppliersText } = useDomainText('suppliers');

export default {
  name: 'SupplierDetailTabsPanel',
  components: {
    AppSectionTabs,
    SupplierDetailOverviewTab,
    SupplierDetailSummaryTab,
    SupplierDetailHistoryTab,
    SupplierDetailPlaceholderTab,
    SupplierDetailExpensesTab,
    SupplierDetailFeesTab,
    SupplierDetailDocumentsTab,
    SupplierDetailAdvancedOptionsTab,
  },
  data() {
    return {
      text: suppliersText,
      operationSubTab: 'expenses',
      financeSubTab: 'payments',
      analysisSubTab: 'historical-flow',
    };
  },
  props: {
    activeTab: {
      type: String,
      required: true,
    },
    tabs: {
      type: Array,
      default: () => [],
    },
    supplier: {
      type: Object,
      default: null,
    },
    supplierOverview: {
      type: Object,
      default: null,
    },
    persons: {
      type: Array,
      default: () => [],
    },
    defaultBillingData: {
      type: Object,
      default: null,
    },
    expenseRows: {
      type: Array,
      default: () => [],
    },
    expenseLoading: {
      type: Boolean,
      default: false,
    },
    expenseFilterOptions: {
      type: Object,
      default: () => ({}),
    },
    expensePagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 30,
        total_pages: 1,
        total_items: 0,
      }),
    },
    expenseStatusTabs: {
      type: Array,
      default: () => [],
    },
    expenseActiveStatusTab: {
      type: String,
      default: 'all',
    },
    expenseSummary: {
      type: Object,
      default: () => ({
        total: 0,
        paid: 0,
        pending: 0,
      }),
    },
    feeRows: {
      type: Array,
      default: () => [],
    },
    feeLoading: {
      type: Boolean,
      default: false,
    },
    feeFilterOptions: {
      type: Object,
      default: () => ({}),
    },
    feePagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 30,
        total_pages: 1,
        total_items: 0,
      }),
    },
    feeStatusTabs: {
      type: Array,
      default: () => [],
    },
    feeActiveStatusTab: {
      type: String,
      default: 'all',
    },
    feeSummary: {
      type: Object,
      default: () => ({
        total: 0,
        paid: 0,
        pending: 0,
      }),
    },
  },
  emits: [
    'edit-section',
    'add-contact',
    'edit-contact',
    'delete-contact',
    'edit-billing-data',
    'open-billing-data-list',
    'create-billing-data',
    'open-history',
    'expenses-search-change',
    'expenses-filters-change',
    'expenses-status-tab-select',
    'expenses-page-change',
    'pay-expense',
    'edit-expense',
    'delete-expense',
    'approve-expense',
    'download-xml',
    'download-pdf',
    'download-receipt',
    'fees-search-change',
    'fees-filters-change',
    'fees-status-tab-select',
    'fees-page-change',
    'pay-fee',
    'edit-fee',
    'delete-fee',
    'approve-fee',
    'download-fee-xml',
    'download-fee-pdf',
    'download-fee-receipt',
    'new-expense-from-supplier',
    'new-fee-from-supplier',
    'open-merge',
    'archive-toggle',
    'delete-supplier',
  ],
  computed: {
    supplierId() {
      return Number(this.supplier?.id || 0) || null;
    },
    operationSubTabs() {
      return createSupplierDetailOperationSubTabs(this.text);
    },
    financeSubTabs() {
      return createSupplierDetailFinanceSubTabs(this.text);
    },
    analysisSubTabs() {
      return createSupplierDetailAnalysisSubTabs(this.text);
    },
    showSubTabs() {
      return this.activeTab === 'operation' || this.activeTab === 'finance' || this.activeTab === 'analysis';
    },
    currentSubTabs() {
      if (this.activeTab === 'operation') {
        return this.operationSubTabs;
      }

      if (this.activeTab === 'finance') {
        return this.financeSubTabs;
      }

      if (this.activeTab === 'analysis') {
        return this.analysisSubTabs;
      }

      return [];
    },
    currentSubTab: {
      get() {
        if (this.activeTab === 'operation') {
          return this.operationSubTab;
        }

        if (this.activeTab === 'finance') {
          return this.financeSubTab;
        }

        if (this.activeTab === 'analysis') {
          return this.analysisSubTab;
        }

        return '';
      },
      set(nextTab) {
        const normalized = String(nextTab || '').trim();
        if (normalized === '') {
          return;
        }

        const availableKeys = this.currentSubTabs.map((tab) => String(tab?.key || ''));
        if (!availableKeys.includes(normalized)) {
          return;
        }

        if (this.activeTab === 'operation') {
          this.operationSubTab = normalized;
          return;
        }

        if (this.activeTab === 'finance') {
          this.financeSubTab = normalized;
          return;
        }

        if (this.activeTab === 'analysis') {
          this.analysisSubTab = normalized;
        }
      },
    },
    currentTabTitle() {
      if (this.showSubTabs) {
        const subTab = this.currentSubTabs.find((item) => String(item?.key || '') === this.currentSubTab);
        return String(subTab?.label || this.text.detail.tabsPanel.fallbackTitle);
      }

      const tab = (Array.isArray(this.tabs) ? this.tabs : []).find((item) => String(item?.key || '') === this.activeTab);
      return String(tab?.label || this.text.detail.tabsPanel.fallbackTitle);
    },
  },
};
</script>
